import { useState, useEffect } from 'react';
import { CreditCard, Zap, TrendingUp, Users, FileText, AlertTriangle, CheckCircle, Clock, ArrowUpRight, Building2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import api from '@/services/api';
import { useAuth } from '@/hooks/useAuth';

interface BillingFeatures {
    invoiceGenerationEnabled: boolean;
    flowEnabled: boolean;
    libreDTEEnabled: boolean;
    mode: 'PRODUCTION' | 'DEMO_NO_BILLING';
}

interface SubscriptionPlan {
    id: number;
    code: string;
    name: string;
    description: string;
    priceCLP: number;
    maxAgencies: number;
    maxUsers: number;
    maxOperationsPerMonth: number;
}

interface Subscription {
    id: number;
    plan: SubscriptionPlan;
    status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED';
    startedAt: string;
    endsAt: string | null;
    renewsAt: string | null;
    billingContactEmail: string;
}

interface UsageMetrics {
    operationsCreated: number;
    criticalNodesDetected: number;
    reportsGenerated: number;
    activeUsers: number;
}

const STATUS_CONFIG = {
    TRIAL:     { labelKey: 'billing.status.trial',     color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
    ACTIVE:    { labelKey: 'billing.status.active',    color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
    PAST_DUE:  { labelKey: 'billing.status.pastDue',   color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
    SUSPENDED: { labelKey: 'billing.status.suspended', color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20' },
    CANCELLED: { labelKey: 'billing.status.cancelled', color: 'text-gray-500', bg: 'bg-gray-800/50 border-gray-700/30' },
};

function daysRemaining(dateStr: string): number {
    const diff = new Date(dateStr).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatCLP(amount: number, freeLabel: string): string {
    if (amount === 0) return freeLabel;
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(amount);
}

function UsageBar({ current, limit, label }: { current: number; limit: number; label: string }) {
    const { t } = useTranslation();
    const unlimited = limit === -1;
    const pct = unlimited ? 0 : Math.min(100, (current / limit) * 100);
    const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-blue-500';

    return (
        <div className="space-y-2">
            <div className="flex justify-between text-xs">
                <span className="text-gray-400 font-bold uppercase tracking-wider">{label}</span>
                <span className="text-white font-black">
                    {current}{unlimited ? '' : ` / ${limit}`}
                    {!unlimited && <span className="text-gray-500 font-normal ml-1">{t('billing.thisMonth')}</span>}
                </span>
            </div>
            {!unlimited && (
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
            )}
        </div>
    );
}

export default function BillingPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [usage, setUsage] = useState<UsageMetrics | null>(null);
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [features, setFeatures] = useState<BillingFeatures | null>(null);
    const [loading, setLoading] = useState(true);
    const [upgrading, setUpgrading] = useState<string | null>(null);

    const isAdmin = user?.roles?.includes('ADMINISTRADOR');

    useEffect(() => {
        Promise.all([
            api.get('/api/v1/billing/subscription').catch(() => ({ data: null })),
            api.get('/api/v1/billing/usage').catch(() => ({ data: null })),
            api.get('/api/v1/billing/plans').catch(() => ({ data: [] })),
            api.get('/api/v1/billing/features').catch(() => ({ data: null })),
        ]).then(([subRes, usageRes, plansRes, featRes]) => {
            setSubscription(subRes.data);
            setUsage(usageRes.data);
            setPlans(plansRes.data || []);
            setFeatures(featRes.data);
        }).finally(() => setLoading(false));
    }, []);

    const handleUpgrade = async (planCode: string) => {
        console.log('[Billing] handleUpgrade called with planCode:', planCode, 'isAdmin:', isAdmin);
        if (!isAdmin) {
            toast.error(t('billing.toast.adminOnly'));
            return;
        }
        setUpgrading(planCode);
        try {
            const res = await api.put(`/api/v1/billing/subscription/upgrade?planCode=${planCode}`);
            console.log('[Billing] upgrade response:', res.status, res.data);
            if (features?.mode === 'DEMO_NO_BILLING') {
                toast.success(t('billing.toast.planActivatedDemo', { planCode }));
            } else {
                toast.success(t('billing.toast.planUpdated', { planCode }));
            }
            const sub = await api.get('/api/v1/billing/subscription');
            setSubscription(sub.data);
        } catch (e: any) {
            console.error('[Billing] upgrade error:', e?.response?.status, e?.response?.data, e);
            toast.error(e?.response?.data?.error || t('billing.toast.upgradeError', { status: e?.response?.status || t('billing.noResponse') }));
        } finally {
            setUpgrading(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const statusCfg = subscription ? STATUS_CONFIG[subscription.status] : null;
    const plan = subscription?.plan;

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                    <CreditCard className="w-8 h-8 text-blue-500" />
                    {t('billing.title')}
                </h1>
                <p className="text-gray-500 mt-2 text-sm font-light">
                    {t('billing.subtitle')}
                </p>
            </div>

            {/* Banner: Modo demo (kill-switches de facturación activos) */}
            {features?.mode === 'DEMO_NO_BILLING' && (
                <div
                    role="status"
                    data-testid="demo-mode-banner"
                    className="p-5 rounded-2xl border border-amber-500/30 bg-amber-500/10 flex items-start gap-4"
                >
                    <div className="shrink-0 mt-0.5">
                        <Info className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-amber-300 uppercase tracking-widest">
                            {t('billing.demoBanner.title')}
                        </div>
                        <p className="text-xs text-amber-200/80 mt-2 leading-relaxed font-light">
                            <Trans i18nKey="billing.demoBanner.body">
                                La activación de planes desde esta página solo modifica el registro interno
                                de la suscripción. <strong className="text-amber-100">No se emiten facturas ni
                                boletas electrónicas, no se cobra a través de Flow.cl y no se envían DTE al SII.</strong>
                                {' '}Los integradores de pago (Flow.cl) y facturación (LibreDTE) están
                                deshabilitados intencionalmente hasta tener clientes con contrato firmado.
                            </Trans>
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                            <span className="px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-300 text-[10px] font-black uppercase tracking-wider">
                                Invoice-gen: OFF
                            </span>
                            <span className="px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-300 text-[10px] font-black uppercase tracking-wider">
                                Flow.cl: OFF
                            </span>
                            <span className="px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-300 text-[10px] font-black uppercase tracking-wider">
                                LibreDTE: OFF
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Current Subscription Card */}
            <div className="p-8 rounded-[2rem] bg-gray-900/40 border border-white/5">
                {subscription && statusCfg && plan ? (
                    <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                        <div className="flex-1">
                            <div className="flex items-center gap-4 mb-4">
                                <h2 className="text-2xl font-black text-white">{plan.name}</h2>
                                <span className={`px-3 py-1 rounded-lg text-xs font-black uppercase border ${statusCfg.bg} ${statusCfg.color}`}>
                                    {t(statusCfg.labelKey)}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                                <div>
                                    <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{t('billing.price')}</div>
                                    <div className="text-white font-black">{formatCLP(plan.priceCLP, t('billing.free'))}<span className="text-gray-500 font-normal text-xs">{t('billing.perMonth')}</span></div>
                                </div>
                                {subscription.status === 'TRIAL' && subscription.endsAt && (
                                    <div>
                                        <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{t('billing.expiresIn')}</div>
                                        <div className={`font-black ${daysRemaining(subscription.endsAt) <= 7 ? 'text-red-400' : 'text-amber-400'}`}>
                                            {t('billing.days', { count: daysRemaining(subscription.endsAt) })}
                                        </div>
                                    </div>
                                )}
                                {subscription.renewsAt && (
                                    <div>
                                        <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{t('billing.nextRenewal')}</div>
                                        <div className="text-white font-bold">{new Date(subscription.renewsAt).toLocaleDateString('es-CL')}</div>
                                    </div>
                                )}
                                <div>
                                    <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{t('billing.billingContact')}</div>
                                    <div className="text-gray-300 text-xs">{subscription.billingContactEmail || '—'}</div>
                                </div>
                            </div>
                        </div>

                        {subscription.status === 'TRIAL' && isAdmin && (
                            <div className="shrink-0 p-5 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex flex-col items-center gap-3 text-center max-w-[200px]">
                                <Clock className="w-8 h-8 text-blue-400" />
                                <p className="text-xs text-gray-400 leading-relaxed">{t('billing.upgradePrompt')}</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">{t('billing.noActiveSubscription')}</p>
                    </div>
                )}
            </div>

            {/* Usage Metrics */}
            {usage && plan && (
                <div className="p-8 rounded-[2rem] bg-gray-900/40 border border-white/5">
                    <h2 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-500" />
                        {t('billing.usageTitle', { period: new Date().toLocaleDateString('es-CL', { month: 'long', year: 'numeric' }) })}
                    </h2>
                    <div className="space-y-5">
                        <UsageBar current={usage.operationsCreated} limit={plan.maxOperationsPerMonth} label={t('billing.usage.operationsCreated')} />
                        <UsageBar current={usage.reportsGenerated} limit={-1} label={t('billing.usage.reportsGenerated')} />
                        <UsageBar current={usage.criticalNodesDetected} limit={-1} label={t('billing.usage.criticalNodes')} />
                        <UsageBar current={usage.activeUsers} limit={plan.maxUsers} label={t('billing.usage.activeUsers')} />
                    </div>
                </div>
            )}

            {/* Plans Grid */}
            <div>
                <h2 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    {t('billing.availablePlans')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {plans.map(p => {
                        const isCurrentPlan = subscription?.plan.code === p.code;
                        const isEnterprise = p.code === 'ENTERPRISE';

                        return (
                            <div
                                key={p.id}
                                className={`p-6 rounded-[2rem] border flex flex-col gap-4 transition-all ${
                                    isCurrentPlan
                                        ? 'bg-blue-500/10 border-blue-500/30'
                                        : 'bg-gray-900/40 border-white/5 hover:border-white/10'
                                }`}
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-black text-gray-400 uppercase tracking-widest">{p.code}</span>
                                        {isCurrentPlan && <CheckCircle className="w-4 h-4 text-blue-400" />}
                                    </div>
                                    <div className="text-xl font-black text-white">{p.name}</div>
                                    <div className="text-2xl font-black text-white mt-2">
                                        {formatCLP(p.priceCLP, t('billing.free'))}
                                        {p.priceCLP > 0 && <span className="text-xs text-gray-500 font-normal">{t('billing.perMonth')}</span>}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">{p.description}</p>
                                </div>

                                <div className="space-y-2 flex-1">
                                    {[
                                        { icon: Building2, label: p.maxAgencies === -1 ? t('billing.feature.agenciesUnlimited') : t('billing.feature.agencies', { count: p.maxAgencies }) },
                                        { icon: Users, label: p.maxUsers === -1 ? t('billing.feature.usersUnlimited') : t('billing.feature.users', { count: p.maxUsers }) },
                                        { icon: FileText, label: p.maxOperationsPerMonth === -1 ? t('billing.feature.opsUnlimited') : t('billing.feature.ops', { count: p.maxOperationsPerMonth }) },
                                    ].map((feat, i) => (
                                        <div key={i} className="flex items-center gap-2 text-xs text-gray-400">
                                            <feat.icon className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                                            {feat.label}
                                        </div>
                                    ))}
                                </div>

                                {!isCurrentPlan && isAdmin && (
                                    <button
                                        data-testid={`upgrade-button-${p.code}`}
                                        aria-label={t('billing.activatePlanAria', { code: p.code })}
                                        onClick={() => isEnterprise
                                            ? toast.info(t('billing.toast.enterpriseContact'))
                                            : handleUpgrade(p.code)
                                        }
                                        disabled={!!upgrading}
                                        title={features?.mode === 'DEMO_NO_BILLING'
                                            ? t('billing.upgradeTitleDemo')
                                            : t('billing.upgradeTitle', { name: p.name })}
                                        className="w-full py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2
                                            bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
                                    >
                                        {upgrading === p.code ? (
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                {isEnterprise
                                                    ? t('billing.contactSales')
                                                    : features?.mode === 'DEMO_NO_BILLING' ? t('billing.activateDemo') : t('billing.upgrade')}
                                                <ArrowUpRight className="w-3.5 h-3.5" />
                                            </>
                                        )}
                                    </button>
                                )}
                                {isCurrentPlan && (
                                    <div className="w-full py-3 rounded-xl text-xs font-black uppercase tracking-widest text-center text-blue-400 bg-blue-500/10 border border-blue-500/20">
                                        {t('billing.currentPlan')}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Contact Banner */}
            <div className="p-6 rounded-[2rem] border border-white/5 bg-white/[0.02] flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-1">
                    <p className="text-sm font-bold text-white">{t('billing.contactBanner.title')}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('billing.contactBanner.subtitle')}</p>
                </div>
                <a
                    href="mailto:ventas@vantak.io"
                    className="shrink-0 px-6 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all border border-white/10"
                >
                    ventas@vantak.io
                </a>
            </div>
        </div>
    );
}
